import { useCallback, RefObject } from 'react';
import * as modelViewer from '@google/model-viewer';
import { Annotation } from '@/types/Annotation.model';
import { AnnotationNavigatorRef } from '@/components/Annotations';
import { ANNOTATION_DEFAULTS } from '@/utils/annotationConstants';
import {
  ANNOTATION_CAMERA_TRANSITION_SETTLE_MS,
  applyAnnotationNavigationCamera,
  resolveAnnotationNavigationCamera,
} from '@/utils/modelViewerZoom';

interface UseAnnotationClickOptions {
  modelViewerRef: RefObject<modelViewer.ModelViewerElement>;
  annotationNavigatorRef: RefObject<AnnotationNavigatorRef>;
  setActiveAnnotation: (annotation: Annotation | null) => void;
  setAnnotationPosition: (position: { x: number; y: number } | null) => void;
  onCloseModals?: () => void;
}

/**
 * Custom hook to handle annotation click events
 * Calculates hotspot position and navigates to annotation
 * @param options - Configuration options for the hook
 * @returns handleAnnotationClick function
 */
export function useAnnotationClick({
  modelViewerRef,
  annotationNavigatorRef,
  setActiveAnnotation,
  setAnnotationPosition,
  onCloseModals,
}: UseAnnotationClickOptions) {
  const handleAnnotationClick = useCallback(
    (annotation: Annotation, event?: React.MouseEvent) => {
      // Close modals when clicking on annotation
      if (onCloseModals) {
        onCloseModals();
      }

      const mv = modelViewerRef.current;

      // If this is an animation point, trigger the selected animation track
      if (mv && annotation.type === 'animation' && annotation.animationTrack) {
        try {
          (mv as any).animationName = annotation.animationTrack;
          mv.play();
        } catch (error) {
          console.error('Error playing animation:', error);
        }
      }

      // Anchor the info box at the click immediately; useAnnotationPositionUpdate tracks camera motion.
      if (event && mv) {
        const viewerRect = mv.getBoundingClientRect();
        setAnnotationPosition({
          x: event.clientX - viewerRect.left,
          y: event.clientY - viewerRect.top,
        });
      }

      // Use AnnotationNavigator's navigateToAnnotation to ensure navigation bar appears
      if (annotationNavigatorRef.current) {
        annotationNavigatorRef.current.navigateToAnnotation(annotation);
      } else {
        // Fallback if navigator is not available
        if (!mv) return;

        // Enable smooth camera transitions
        (mv as any).interactionPrompt = 'none';

        // Temporarily disable camera-controls to allow programmatic orbit changes
        const hadCameraControls = mv.hasAttribute('camera-controls');
        if (hadCameraControls) {
          mv.removeAttribute('camera-controls');
        }

        const cameraState = resolveAnnotationNavigationCamera(mv, annotation, {
          orbit: ANNOTATION_DEFAULTS.MODEL_VIEWER_ORBIT,
          fieldOfView: ANNOTATION_DEFAULTS.FIELD_OF_VIEW,
        });

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            applyAnnotationNavigationCamera(mv, cameraState);

            if (hadCameraControls) {
              setTimeout(() => {
                mv.setAttribute('camera-controls', '');
              }, ANNOTATION_CAMERA_TRANSITION_SETTLE_MS);
            }
          });
        });

        setActiveAnnotation(annotation);
      }
    },
    [modelViewerRef, annotationNavigatorRef, setActiveAnnotation, setAnnotationPosition, onCloseModals]
  );

  return handleAnnotationClick;
}

